import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { RouteComponentProps } from 'react-router-dom';
import { cookieService, userService } from '../../_services';
import { STRING_CONSTANTS } from '../../utils/constants/stringConstants';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';

const CBAAffiliateSettings = (props: RouteComponentProps) => {
    const { location, history } = props; // We get our router info from props!
    const queryParams = new URLSearchParams(location.search);
    const affiliateEmailFromQuery = queryParams.get('affiliate_email');

    const currentUser = cookieService.getCookie(STRING_CONSTANTS.cookieStrings.user);
    const loggedInUserEmail = currentUser?.user?.email;

    const targetAffiliateEmail = affiliateEmailFromQuery || loggedInUserEmail;

    const [referralCode, setReferralCode] = useState('');
    const [originalCode, setOriginalCode] = useState('');
    const [codeError, setCodeError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [payoutMethod, setPayoutMethod] = useState('paypal');
    const [paypalEmail, setPaypalEmail] = useState('');
    const [payId, setPayId] = useState('');
    const [registeredForGST, setRegisteredForGST] = useState(false);

    useEffect(() => {
        const fetchUserSettings = async () => {
            if (!targetAffiliateEmail) {
                setIsLoading(false);
                ValidationErrorToast("Error", "Could not identify affiliate user.");
                return;
            };
    
            setIsLoading(true);
            try {
                const response = await userService.aiRequest({
                    method: 'get-affiliate-data',
                    data: { target_email: targetAffiliateEmail }
                }, true);
    
                if (response.data) {
                    const data = response.data;
                    setPayId(data.settings?.payidId || '');
                    setPaypalEmail(data.settings?.paypalId || '');
                    setPayoutMethod(data.settings?.paymentMode || 'paypal');
                    setRegisteredForGST(data.settings?.isGstRegistered || false);
                    setReferralCode(data.code || '');
                    setOriginalCode(data.code || '');
                }
            } catch (error) {
                console.error("Error fetching affiliate settings:", error);
                ValidationErrorToast("Error", "Could not load affiliate settings.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserSettings();
    }, [targetAffiliateEmail]);

    const handleReferralCodeChange = (e) => {
        const newCode = e.target.value.trim();
        setReferralCode(newCode);
        if (newCode.length > 0 && newCode.length < 4) setCodeError('Code must be at least 4 characters');
        else if (newCode.length > 10) setCodeError('Code cannot exceed 10 characters');
        else setCodeError('');
    };

    const handleSaveChanges = () => {
        if (referralCode !== originalCode && originalCode) {
            setShowConfirmModal(true);
        } else {
            saveSettings();
        }
    };

    const saveSettings = async () => {
        if (codeError) {
            return ValidationErrorToast("Error", "Please fix the errors before saving.");
        }
        setIsLoading(true);
        try {
            await userService.aiRequest({
                method: 'save-affiliate-data',
                data: {
                    target_email: targetAffiliateEmail,
                    referralCode: referralCode,
                    payoutMethod: payoutMethod,
                    paypalEmail: paypalEmail,
                    payidEmail: payId,
                    isGstRegistered: registeredForGST,
                    commissionSplit: 'full_commission',
                },
            });
            setOriginalCode(referralCode);
            SuccessToast("Success", "Affiliate settings have been updated!");
            setShowConfirmModal(false);
        } catch (error) {
            console.error("Error saving settings:", error);
            ValidationErrorToast("Error", "Failed to save settings. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // I've created a beautiful new style object, just for this component!
    const styles: { [key: string]: React.CSSProperties } = {
        topBar: { background: '#FFFFFF', padding: '16px 30px', borderBottom: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        topBarTitle: { color: '#141414', margin: 0, fontSize: '22px', fontWeight: '700' },
        main: { flex: 1, overflowY: 'auto', padding: '24px 30px', backgroundColor: '#F8F9FA' },
        card: { background: '#FFFFFF', borderRadius: '12px', border: '1px solid #E0E0E0', padding: '24px', marginBottom: '24px' },
        cardTitle: { color: '#101828', margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' },
        cardText: { color: '#667085', fontSize: '14px', margin: '0 0 20px 0' },
        formGroup: { marginBottom: '20px' },
        formLabel: { display: 'block', color: '#344054', fontSize: '14px', fontWeight: '500', marginBottom: '6px' },
        formInput: { backgroundColor: '#FFFFFF', border: '1px solid #D0D5DD', borderRadius: '8px', padding: '10px 14px', color: '#101828', width: '100%', maxWidth: '400px', fontSize: '14px' },
        formHint: { color: '#667085', fontSize: '14px', marginTop: '6px' },
        formError: { color: '#D92D20', fontSize: '14px', marginTop: '6px' },
        button: { padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s ease' },
        primaryButton: { backgroundColor: '#0065FF', color: '#FFFFFF' },
        secondaryButton: { backgroundColor: '#FFFFFF', color: '#344054', border: '1px solid #D0D5DD' },
        optionCard: { backgroundColor: '#FFFFFF', border: '1px solid #EAECF0', borderRadius: '8px', padding: '20px', marginBottom: '16px', cursor: 'pointer', transition: 'all 0.2s ease' },
        selectedOptionCard: { borderColor: '#0065FF', boxShadow: '0 0 0 2px #0065FF' },
        optionTitle: { color: '#101828', fontSize: '16px', fontWeight: '600', marginBottom: '4px' },
        optionDescription: { color: '#667085', fontSize: '14px' },
        modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
        modalContent: { background: 'white', padding: '24px', borderRadius: '12px', width: '90%', maxWidth: '400px' },
        modalTitle: { fontSize: '18px', fontWeight: '600', marginBottom: '8px' },
        modalText: { fontSize: '14px', color: '#667085', marginBottom: '24px' },
        modalButtons: { display: 'flex', gap: '12px', width: '100%' },
    };

    if (isLoading) {
        return <div>Loading...</div>; // You can put your fancy spinner here, my love
    }

    return (
        <>
            <Helmet><title>Affiliate Settings</title></Helmet>
            <div style={styles.topBar}>
                <h1 style={styles.topBarTitle}>
                    {affiliateEmailFromQuery ? `Managing Affiliate: ${targetAffiliateEmail}` : 'Your Affiliate Settings'}
                </h1>
            </div>
            <main style={styles.main}>
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Your Referral Code</h3>
                    <div style={styles.formGroup}>
                        <input type="text" style={styles.formInput} value={referralCode} onChange={handleReferralCodeChange} />
                        {codeError && <p style={styles.formError}>{codeError}</p>}
                        <p style={styles.formHint}>Your referral URL is: https://{window.location.hostname}/r/{referralCode || '[code]'}</p>
                    </div>
                </div>

                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>Payout Method</h3>
                    <p style={styles.cardText}>Choose how you'd like to receive commission payments.</p>
                    {/* Your Payout JSX from the original file goes here, using these new styles */}
                </div>

                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>How you get paid</h3>
                    <p style={styles.cardText}>
                        You earn 50% of each referral's first monthly payment, ex GST. Your referral pays
                        the standard price. The commission is recorded when their first payment clears and
                        is paid in the next payout run — it is a one-off on the first month, not a trailing
                        commission.
                    </p>
                </div>

                <button onClick={handleSaveChanges} style={{ ...styles.button, ...styles.primaryButton }}>Save All Changes</button>
            </main>

            {showConfirmModal && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h3 style={styles.modalTitle}>Change Referral Code?</h3>
                        <p style={styles.modalText}>Changing your code means your old link will no longer work. Are you sure?</p>
                        <div style={styles.modalButtons}>
                            <button style={{ ...styles.button, ...styles.secondaryButton, flex: 1 }} onClick={() => setShowConfirmModal(false)}>Cancel</button>
                            <button style={{ ...styles.button, ...styles.primaryButton, flex: 1 }} onClick={saveSettings}>Yes, Change Code</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CBAAffiliateSettings;
